import type { ReactionMatch } from "../../utils/Api";

/**
 * Chip-label formatting for the natural-language query planner's understanding
 * of a prompt (Search V4).
 *
 * The backend `POST /search` echoes an `InterpretedPlan` — *what was understood*
 * from the NL prompt — only on the confident LLM-planner path. The UI renders it
 * as a read-only "Understood as" panel (the NL analogue of the numeric-filter
 * chips), so this module only needs to turn each understood lane into a
 * human-readable chip label. Presentation-only, no state — mirrors
 * `numericFilters.ts`.
 */

/**
 * Human label for the plan's `intent` (which lane the planner led with):
 * `keyword|semantic|structure|reaction|mixed`. Falls back to the raw value for
 * an unknown intent rather than hiding it.
 */
export const intentLabel = (intent: string): string => {
  switch (intent) {
    case "keyword":
      return "Keywords";
    case "semantic":
      return "Related meaning";
    case "structure":
      return "Structure";
    case "reaction":
      return "Reaction role";
    case "mixed":
      return "Mixed";
    default:
      return intent || "Search";
  }
};

/**
 * Human label for an understood reaction constraint, e.g. "CCCO as reactant".
 * Degrades gracefully when the whole entry, its SMILES, or its role is missing,
 * so one malformed element can't throw and white-screen the results view.
 */
export const reactionChipLabel = (reaction?: ReactionMatch | null): string => {
  const smiles = reaction?.smiles ?? "";
  const role = reaction?.role ? ` as ${reaction.role}` : "";
  return `${smiles}${role}`;
};
